import React, { useState, useEffect, useMemo, useCallback } from 'react';
import notificationService, { type Notification, type NotificationType } from '../services/notificationService';
import '../i18n/config';
import { useI18n } from '../contexts/I18nContext';

const NotificationHistory: React.FC = () => {
  const { t } = useI18n();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | NotificationType>('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const PAGE_SIZE = 20;

  const sortNotifications = useCallback((items: Notification[]) => {
    return [...items].sort((a, b) => {
      const aTime = a.timestamp ?? (a.created_at ? new Date(a.created_at).getTime() : 0);
      const bTime = b.timestamp ?? (b.created_at ? new Date(b.created_at).getTime() : 0);
      return bTime - aTime;
    });
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Erro ao atualizar contagem de notificações não lidas:', err);
    }
  }, []);

  const loadNotifications = useCallback(
    async (reset = false) => {
      if (reset) {
        setIsLoading(true);
        setOffset(0);
        setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }

      setError(null);
      const currentOffset = reset ? 0 : offset;

      try {
        const result = await notificationService.getNotificationHistory(PAGE_SIZE, currentOffset);
        const fetched = sortNotifications(result.notifications);

        setNotifications((prev) => {
          if (reset) {
            return fetched;
          }

          const merged = new Map<string, Notification>();

          prev.forEach((item) => {
            const key = item.id ?? (item.timestamp ? `ts-${item.timestamp}` : `title-${item.title}`);
            merged.set(key, item);
          });

          fetched.forEach((item) => {
            const key = item.id ?? (item.timestamp ? `ts-${item.timestamp}` : `title-${item.title}`);
            merged.set(key, { ...merged.get(key), ...item });
          });

          return sortNotifications(Array.from(merged.values()));
        });

        const addedCount = fetched.length;
        setOffset(currentOffset + addedCount);
        setHasMore(addedCount === PAGE_SIZE);

      } catch (err: unknown) {
        console.error('Erro ao carregar histórico:', err);
        setError(err instanceof Error ? err.message : t('notifications.history.error.loading'));
      } finally {
        if (reset) {
          setIsLoading(false);
        }
        setIsLoadingMore(false);
      }
    },
    [offset, PAGE_SIZE, sortNotifications, t]
  );

  useEffect(() => {
    loadNotifications(true);
  }, [loadNotifications]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  const filteredNotifications = useMemo(() => {
    if (filterType === 'all') {
      return notifications;
    }
    return notifications.filter((notification) => notification.type === filterType);
  }, [notifications, filterType]);

  const hasUnread = useMemo(() => notifications.some((n) => !n.read), [notifications]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
        )
      );
      refreshUnreadCount();
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
      setError(t('notifications.history.error.markRead'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, read_at: new Date().toISOString() })));
      refreshUnreadCount();
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
      setError(t('notifications.history.error.markAllRead'));
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      refreshUnreadCount();
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError(t('notifications.history.error.delete'));
    }
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as 'all' | NotificationType;
    setFilterType(value);
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadNotifications();
    }
  };

  const formatDate = (notification: Notification) => {
    const timestamp =
      notification.timestamp ??
      (notification.created_at ? new Date(notification.created_at).getTime() : undefined);
    if (!timestamp) return '';

    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'success': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#2563eb';
    }
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>{t('notifications.history.loading')}</div>;
  }

  const filterOptions: Array<{ value: 'all' | NotificationType; label: string }> = [
    { value: 'all', label: t('notifications.history.all') },
    { value: 'info', label: t('notifications.history.info') },
    { value: 'success', label: t('notifications.history.success') },
    { value: 'warning', label: t('notifications.history.warning') },
    { value: 'error', label: t('notifications.history.error') },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <h2 style={{ margin: 0 }}>{t('notifications.history.title')}</h2>
          <div style={{ fontSize: '14px', color: '#4b5563' }}>
            {t('notifications.history.unread')}{' '}
            <span style={{ padding: '2px 8px', backgroundColor: unreadCount > 0 ? '#fee2e2' : '#e5e7eb', color: unreadCount > 0 ? '#b91c1c' : '#374151', borderRadius: '999px', fontWeight: 600 }}>
              {unreadCount}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="notification-filter-type" style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', color: '#6b7280' }}>
            {t('notifications.history.filterByType')}
            <select
              id="notification-filter-type"
              name="notification-filter-type"
              value={filterType}
              onChange={handleFilterChange}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                minWidth: '180px',
              }}
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

        {hasUnread && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {t('notifications.history.markAllRead')}
          </button>
        )}
        </div>
      </div>

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          borderRadius: '8px', 
          marginBottom: '20px',
          color: '#c00'
        }}>
          {error}
        </div>
      )}

      {filteredNotifications.length === 0 ? (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f9fafb',
          borderRadius: '8px'
        }}>
          {t('notifications.history.none')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id || notification.timestamp}
              style={{
                padding: '16px',
                backgroundColor: notification.read ? '#f9fafb' : '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                borderLeft: `4px solid ${getTypeColor(notification.type)}`,
                cursor: 'pointer',
                opacity: notification.read ? 0.7 : 1
              }}
              onClick={() => {
                if (notification.id && !notification.read) {
                  handleMarkAsRead(notification.id);
                }
                if (notification.url) {
                  window.location.href = notification.url;
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: notification.read ? 'normal' : 'bold' }}>
                      {notification.title}
                    </h3>
                    {!notification.read && (
                      <span style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#2563eb',
                        borderRadius: '50%',
                        display: 'inline-block'
                      }} />
                    )}
                  </div>
                  <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                    {notification.body}
                  </p>
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#999' 
                  }}>
                    {formatDate(notification)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {notification.id && !notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id!);
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {t('notifications.history.markAsRead')}
                    </button>
                  )}
                  {notification.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id!);
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#c00'
                      }}
                    >
                      {t('notifications.history.delete')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: isLoadingMore ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: '#111827',
            }}
          >
            {isLoadingMore ? t('notifications.history.loading') : t('notifications.history.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;

